import * as React from "react"
import { CheckCircle2, Circle, Copy, ExternalLink, Loader2, Send } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { CopyButton, copyToClipboard } from "@/components/ui/copy-button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/searchable-select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useBookingT } from "@/hooks/use-translations"
import { formatUserDateTime } from "@/lib/date-time"
import { formatMoneyAmount } from "@/lib/money"
import {
  activate,
  disconnect,
  fetchCompanies,
  fetchConnections,
  fetchExports,
  fetchSetup,
  pause,
  runNow,
  startConsent,
  updateSettings,
  type AccountingConnection,
  type AccountingExportRow,
  type AccountingKind,
  type AccountingSettings,
  type AccountingSetup,
  type AccountingView,
  type ExportKind,
  type LedgerCompany,
} from "@/features/integrations/accounting/accountingApi"

/** Our Entra app: the customer enables this client id on BC's "Microsoft Entra applications" page. */
const CLIENT_ID = "37d1950b-028c-42dc-8de9-b3ee40bc7a82"
const NONE = "__none__"

type Props = {
  providerName: string
  view: AccountingView
  onNavigate: (view: AccountingView) => void
}

function useKindLabels(): Record<AccountingKind, string> {
  const t = useBookingT()
  return {
    flight: t("acc_kind_flight", "Flights"),
    hotel: t("acc_kind_hotel", "Hotels"),
    train: t("acc_kind_train", "Trains"),
    car: t("acc_kind_car", "Car rental"),
    taxi: t("acc_kind_taxi", "Taxis and transfers"),
    meals: t("acc_kind_meals", "Meals"),
    other: t("acc_kind_other", "Fees and everything else"),
  }
}

/**
 * Business Central on the integration page, in tabs: Overview (connect steps,
 * what is sent, switch on), Settings (company, vendor, accounts, employees)
 * and Activity (what went, what failed and why). Travel Code only posts:
 * invoices as purchase invoices, approved reimbursements as journal lines
 * (docs/integrations/microsoft/business-central.md).
 */
export function AccountingPanel({ providerName, view, onNavigate }: Props) {
  const t = useBookingT()
  const [connection, setConnection] = React.useState<AccountingConnection | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [companies, setCompanies] = React.useState<LedgerCompany[] | null>(null)
  const [setupHint, setSetupHint] = React.useState<string | null>(null)

  const checkCompanies = React.useCallback(async (id: number) => {
    try {
      setCompanies(await fetchCompanies(id))
      setSetupHint(null)
    } catch (e) {
      setCompanies([])
      setSetupHint((e as Error).message)
    }
  }, [])

  const load = React.useCallback(
    () =>
      fetchConnections().then((list) => {
        const first = list[0] ?? null
        setConnection(first)
        if (first) void checkCompanies(first.id)
        return first
      }),
    [checkCompanies],
  )

  React.useEffect(() => {
    load()
      .catch((e) => toast.error((e as Error).message))
      .finally(() => setLoading(false))
  }, [load])

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    )
  }

  if (!connection) {
    return (
      <ConnectCard
        providerName={providerName}
        onApproved={() => {
          void load()
          toast.success(t("acc_consent_done", "Microsoft approved Travel Code."))
        }}
        poll={load}
      />
    )
  }

  return (
    <div className="space-y-6">
      <Tabs value={view} onValueChange={(v) => onNavigate(v as AccountingView)}>
        <TabsList variant="underline" className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" variant="underline">
            {t("acc_tab_overview", "Overview")}
          </TabsTrigger>
          <TabsTrigger value="settings" variant="underline">
            {t("acc_tab_settings", "Settings")}
          </TabsTrigger>
          <TabsTrigger value="activity" variant="underline">
            {t("acc_tab_activity", "Activity")}
            {(connection.counts.invoice?.failed ?? 0) + (connection.counts.expense?.failed ?? 0) > 0 && (
              <span className="ml-1.5 rounded-full bg-warning-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {(connection.counts.invoice?.failed ?? 0) + (connection.counts.expense?.failed ?? 0)}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "overview" && (
        <OverviewCard
          connection={connection}
          companies={companies}
          setupHint={setupHint}
          onChange={setConnection}
          onRecheck={() => void checkCompanies(connection.id)}
          onDisconnected={() => {
            setConnection(null)
            setCompanies(null)
          }}
          onNavigate={onNavigate}
        />
      )}
      {view === "settings" && (
        <SettingsCard connection={connection} companies={companies ?? []} setupHint={setupHint} onChange={setConnection} />
      )}
      {view === "activity" && <ActivityCard connection={connection} />}
    </div>
  )
}

type ConnectProps = {
  providerName: string
  /** Re-reads the connections; resolves with the connection once an administrator approved. */
  poll: () => Promise<AccountingConnection | null>
  onApproved: () => void
}

/**
 * The person connecting is often not the Microsoft administrator: the link can
 * be opened here or sent to them, and the page waits for the approval wherever
 * it happens (the callback binds the tenant to this account by itself).
 */
function ConnectCard({ providerName, poll, onApproved }: ConnectProps) {
  const t = useBookingT()
  const [busy, setBusy] = React.useState(false)
  const [link, setLink] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!link) return
    const timer = window.setInterval(() => {
      poll()
        .then((found) => {
          if (found) {
            window.clearInterval(timer)
            onApproved()
          }
        })
        .catch(() => undefined)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [link, poll, onApproved])

  const issue = async (): Promise<string | null> => {
    if (link) return link
    setBusy(true)
    try {
      const url = (await startConsent()).consentUrl
      setLink(url)
      return url
    } catch (e) {
      toast.error((e as Error).message)
      return null
    } finally {
      setBusy(false)
    }
  }

  const openHere = async () => {
    const url = await issue()
    if (url) window.open(url, "_blank", "noopener")
  }

  const copyForAdmin = async () => {
    const url = await issue()
    if (url) await copyToClipboard(url, t("acc_link_copied", "Link copied. Send it to whoever administers your Microsoft tenant; it is valid for 3 days."))
  }

  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="space-y-1.5">
          <h2 className="text-lg font-semibold text-neutral-900">{t("acc_connect_title", "Connect {name}").replace("{name}", providerName)}</h2>
          <p className="text-sm text-neutral-800">
            {t(
              "acc_connect_text",
              "Travel Code posts to Business Central only: your invoices as purchase invoices and approved reimbursements as journal lines. Nothing is sent until you choose the accounts and switch it on.",
            )}
          </p>
        </div>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-neutral-900">
          <li>{t("acc_step_consent", "An administrator of your Microsoft tenant approves Travel Code and signs in once to confirm. If that is someone else, send them the link.")}</li>
          <li>{t("acc_step_enable", "In Business Central, Travel Code is added on the Microsoft Entra applications page. We show you how.")}</li>
          <li>{t("acc_step_setup", "You pick the company, the Travel Code vendor and your accounts, then switch it on.")}</li>
        </ol>

        {link && (
          <div className="space-y-2 rounded-md border border-neutral-200 bg-neutral-50 p-4">
            <p className="flex items-center gap-2 text-sm font-medium text-neutral-900">
              <Loader2 className="size-4 animate-spin" />
              {t("acc_waiting", "Waiting for the approval in Microsoft. This page updates by itself.")}
            </p>
            <p className="break-all text-xs text-neutral-700">{link}</p>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => void copyForAdmin()} disabled={busy}>
            <Copy className="size-4" />
            {t("acc_copy_link", "Copy link for your Microsoft admin")}
          </Button>
          <Button onClick={() => void openHere()} disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <ExternalLink className="size-4" />}
            {t("acc_connect_go", "I am the admin: open Microsoft")}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function EnableInBcSteps({ hint, onRecheck }: { hint: string | null; onRecheck: () => void }) {
  const t = useBookingT()
  return (
    <div className="space-y-3 rounded-md border border-warning-300 bg-warning-50 p-4">
      <p className="text-sm font-medium text-neutral-900">{t("acc_enable_title", "One step left in Business Central")}</p>
      {hint && <p className="text-sm text-neutral-800">{hint}</p>}
      <ol className="list-decimal space-y-1.5 pl-5 text-sm text-neutral-900">
        <li>{t("acc_enable_1", "In Business Central, search for “Microsoft Entra applications” and choose New.")}</li>
        <li>
          <span>{t("acc_enable_2", "Client ID:")} </span>
          <code className="rounded bg-white px-1.5 py-0.5 text-xs">{CLIENT_ID}</code>
          <CopyButton value={CLIENT_ID} className="ml-1 align-middle" />
        </li>
        <li>{t("acc_enable_3", "Description: Travel Code. State: Enabled.")}</li>
        <li>{t("acc_enable_4", "Under User Permission Sets add D365 BUS FULL ACCESS, leave Company empty.")}</li>
      </ol>
      <Button variant="outline" size="sm" onClick={onRecheck}>
        {t("acc_enable_check", "Check again")}
      </Button>
    </div>
  )
}

type OverviewProps = {
  connection: AccountingConnection
  companies: LedgerCompany[] | null
  setupHint: string | null
  onChange: (next: AccountingConnection) => void
  onRecheck: () => void
  onDisconnected: () => void
  onNavigate: (view: AccountingView) => void
}

function OverviewCard({ connection, companies, setupHint, onChange, onRecheck, onDisconnected, onNavigate }: OverviewProps) {
  const t = useBookingT()
  const [busy, setBusy] = React.useState(false)
  const [confirmDisconnect, setConfirmDisconnect] = React.useState(false)
  const s = connection.settings
  const reachable = companies !== null && companies.length > 0
  const configured = s.companyId !== null && (s.invoices || s.expenses)

  const act = async (fn: () => Promise<AccountingConnection | void>, done?: string) => {
    setBusy(true)
    try {
      const next = await fn()
      if (next) onChange(next)
      if (done) toast.success(done)
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const statusBadge =
    connection.status === "active" ? (
      <Badge variant="green">{t("acc_status_active", "Sending")}</Badge>
    ) : connection.status === "paused" ? (
      <Badge variant="secondary">{t("acc_status_paused", "Paused")}</Badge>
    ) : (
      <Badge variant="outline">{t("acc_status_draft", "Not switched on yet")}</Badge>
    )

  const flow = (label: string, on: boolean, counts: AccountingConnection["counts"]["invoice"]) => (
    <div className="flex items-center justify-between gap-4 py-2.5 max-md:flex-col max-md:items-start">
      <span className="text-sm font-medium text-neutral-900">{label}</span>
      <span className="text-sm text-neutral-800">
        {on
          ? t("acc_counts", "{sent} sent · {failed} not sent")
              .replace("{sent}", String(counts?.sent ?? 0))
              .replace("{failed}", String(counts?.failed ?? 0))
          : t("acc_flow_off", "Off")}
      </span>
    </div>
  )

  const step = (done: boolean, label: string) => (
    <li className="flex items-center gap-2 text-sm text-neutral-900">
      {done ? <CheckCircle2 className="size-4 text-success-600" /> : <Circle className="size-4 text-neutral-400" />}
      {label}
    </li>
  )

  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="flex items-center justify-between gap-4 max-md:flex-col max-md:items-start">
          <div className="flex flex-wrap items-center gap-2">
            {statusBadge}
            <span className="text-sm text-neutral-900">{s.companyName ?? t("acc_no_company", "No company chosen yet")}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {connection.status === "active" ? (
              <>
                <Button variant="outline" disabled={busy} onClick={() => void act(async () => void (await runNow(connection.id)), t("acc_run_queued", "Sending. The activity updates in a minute."))}>
                  <Send className="size-4" />
                  {t("acc_run", "Send now")}
                </Button>
                <Button variant="outline" disabled={busy} onClick={() => void act(() => pause(connection.id), t("acc_paused", "Paused. Nothing is sent until you switch it on."))}>
                  {t("acc_pause", "Pause")}
                </Button>
              </>
            ) : (
              <Button disabled={busy || !configured || !reachable} onClick={() => void act(() => activate(connection.id), t("acc_activated", "Switched on. Documents go out every night."))}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                {t("acc_activate", "Switch on")}
              </Button>
            )}
          </div>
        </div>

        {connection.lastError && <p className="text-sm text-error-700">{connection.lastError}</p>}

        <div className="divide-y divide-neutral-200 border-y border-neutral-200">
          {flow(t("acc_flow_invoices", "Invoices"), s.invoices, connection.counts.invoice)}
          {s.invoices && (connection.counts.credit?.sent ?? 0) + (connection.counts.credit?.failed ?? 0) > 0 &&
            flow(t("acc_flow_credits", "Credit memos"), true, connection.counts.credit)}
          {flow(t("acc_flow_expenses", "Reimbursements"), s.expenses, connection.counts.expense)}
        </div>
        {connection.lastExportAt && (
          <p className="text-sm text-neutral-800">
            {t("acc_last_sent", "Last sent: {when}").replace("{when}", formatUserDateTime(new Date(connection.lastExportAt * 1000).toISOString()))}
          </p>
        )}

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-neutral-900">{t("acc_setup", "Setup")}</h3>
          <ul className="space-y-1.5">
            {step(true, t("acc_setup_consent", "Approved in Microsoft"))}
            {step(reachable, t("acc_setup_enabled", "Enabled in Business Central"))}
            {step(configured, t("acc_setup_accounts", "Company, vendor and accounts chosen"))}
          </ul>
          {!reachable && <EnableInBcSteps hint={setupHint} onRecheck={onRecheck} />}
          {reachable && !configured && (
            <Button variant="outline" size="sm" onClick={() => onNavigate("settings")}>
              {t("acc_go_settings", "Choose company and accounts")}
            </Button>
          )}
        </div>

        <div className="flex justify-end border-t border-neutral-200 pt-4">
          <Button variant="ghost" className="text-error-700" onClick={() => setConfirmDisconnect(true)}>
            {t("acc_disconnect", "Disconnect")}
          </Button>
        </div>
      </CardContent>

      <Dialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader className="text-left">
            <DialogTitle>{t("acc_disconnect_title", "Disconnect Business Central?")}</DialogTitle>
            <DialogDescription className="text-neutral-800">
              {t("acc_disconnect_text", "Nothing more is sent and the setup is forgotten. What is already in Business Central stays there. To remove access completely, also delete Travel Code from Microsoft Entra applications in Business Central.")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDisconnect(false)}>
              {t("acc_cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  await disconnect(connection.id)
                  setConfirmDisconnect(false)
                  onDisconnected()
                }, t("acc_disconnected", "Disconnected."))
              }
            >
              {t("acc_disconnect", "Disconnect")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

type SettingsProps = {
  connection: AccountingConnection
  companies: LedgerCompany[]
  setupHint: string | null
  onChange: (next: AccountingConnection) => void
}

function SettingsCard({ connection, companies, setupHint, onChange }: SettingsProps) {
  const t = useBookingT()
  const kindLabel = useKindLabels()
  const [draft, setDraft] = React.useState<AccountingSettings>(connection.settings)
  const [setup, setSetup] = React.useState<AccountingSetup | null>(null)
  const [setupError, setSetupError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (!draft.companyId) return
    setSetup(null)
    setSetupError(null)
    fetchSetup(connection.id, draft.companyId)
      .then((data) => {
        setSetup(data)
        setDraft((d) => ({
          ...d,
          invoiceAccounts: Object.keys(d.invoiceAccounts).length > 0 ? d.invoiceAccounts : data.suggestedAccounts,
          expenseAccounts: Object.keys(d.expenseAccounts).length > 0 ? d.expenseAccounts : data.suggestedAccounts,
          employeeMap: {
            ...Object.fromEntries(data.travellers.filter((tr) => tr.suggested).map((tr) => [String(tr.id), tr.suggested as string])),
            ...d.employeeMap,
          },
        }))
      })
      .catch((e) => setSetupError((e as Error).message))
  }, [connection.id, draft.companyId])

  if (companies.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <EnableInBcSteps hint={setupHint} onRecheck={() => window.location.reload()} />
        </CardContent>
      </Card>
    )
  }

  const save = async () => {
    setBusy(true)
    try {
      onChange(await updateSettings(connection.id, draft))
      toast.success(t("acc_saved", "Saved."))
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const accountOptions: SearchableSelectOption[] = (setup?.accounts ?? []).map((a) => ({ value: a.number, label: `${a.number} ${a.name}`, hint: a.category }))
  const accountsBlock = (field: "invoiceAccounts" | "expenseAccounts") => (
    <div className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
      {(setup?.kinds ?? []).map((kind) => (
        <div key={kind} className="flex items-center justify-between gap-4 px-4 py-2.5 max-md:flex-col max-md:items-stretch">
          <span className="text-sm text-neutral-900">{kindLabel[kind]}</span>
          <SearchableSelect
            className="w-80 max-md:w-full"
            options={[{ value: NONE, label: kind === "other" ? t("acc_account_none", "Not chosen") : t("acc_account_as_other", "Same as “Fees and everything else”") }, ...accountOptions]}
            value={draft[field][kind] ?? NONE}
            onChange={(v) => setDraft((d) => ({ ...d, [field]: { ...d[field], [kind]: v === NONE ? undefined : v } }))}
            searchPlaceholder={t("acc_search_account", "Search by number or name")}
          />
        </div>
      ))}
    </div>
  )

  return (
    <Card>
      <CardContent className="space-y-6 p-6">
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-neutral-900">{t("acc_company", "Business Central company")}</h3>
          <SearchableSelect
            className="w-96 max-md:w-full"
            options={companies.map((c) => ({ value: c.id, label: c.name }))}
            value={draft.companyId ?? ""}
            placeholder={t("acc_company_pick", "Choose a company")}
            onChange={(v) => setDraft((d) => ({ ...d, companyId: v, companyName: companies.find((c) => c.id === v)?.name ?? null, vendorNumber: null, journalCode: null, invoiceAccounts: {}, expenseAccounts: {} }))}
          />
        </section>

        {draft.companyId && !setup && !setupError && <Skeleton className="h-48 rounded-lg" />}
        {setupError && <p className="text-sm text-error-700">{setupError}</p>}

        {setup && (
          <>
            {setup.agencies.length > 1 && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-neutral-900">{t("acc_agencies", "Which of your companies send here")}</h3>
                <p className="text-sm text-neutral-800">{t("acc_agencies_hint", "None ticked means all of them.")}</p>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {setup.agencies.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm text-neutral-900">
                      <Checkbox
                        checked={draft.agencyIds.includes(a.id)}
                        onCheckedChange={(on) => setDraft((d) => ({ ...d, agencyIds: on ? [...d.agencyIds, a.id] : d.agencyIds.filter((x) => x !== a.id) }))}
                      />
                      {a.name}
                    </label>
                  ))}
                </div>
              </section>
            )}

            <section className="space-y-3 border-t border-neutral-200 pt-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900">{t("acc_invoices", "Invoices as purchase invoices")}</h3>
                  <p className="text-sm text-neutral-800">
                    {t("acc_invoices_hint", "Each Travel Code invoice issued after you switch this on, line by line, with the cost center and the PDF.")}
                  </p>
                </div>
                <Switch checked={draft.invoices} onCheckedChange={(on) => setDraft((d) => ({ ...d, invoices: on }))} aria-label={t("acc_invoices", "Invoices as purchase invoices")} />
              </div>
              {draft.invoices && (
                <>
                  <div className="flex items-center justify-between gap-4 max-md:flex-col max-md:items-stretch">
                    <div>
                      <span className="text-sm text-neutral-900">{t("acc_vendor", "Travel Code vendor")}</span>
                      <p className="text-xs text-neutral-700">{t("acc_vendor_hint", "Create a vendor “Travel Code” in Business Central if you have none: one created by an app has no posting group.")}</p>
                    </div>
                    <SearchableSelect
                      className="w-80 max-md:w-full"
                      options={setup.vendors.map((v) => ({ value: v.number, label: `${v.number} ${v.name}` }))}
                      value={draft.vendorNumber ?? ""}
                      placeholder={t("acc_vendor_pick", "Choose the vendor")}
                      onChange={(v) => setDraft((d) => ({ ...d, vendorNumber: v }))}
                    />
                  </div>
                  {accountsBlock("invoiceAccounts")}
                  <label className="flex items-center gap-2 text-sm text-neutral-900">
                    <Checkbox checked={draft.postInvoices} onCheckedChange={(on) => setDraft((d) => ({ ...d, postInvoices: on === true }))} />
                    {t("acc_post_invoices", "Post the invoices (otherwise they wait as drafts for accounts payable)")}
                  </label>
                </>
              )}
            </section>

            <section className="space-y-3 border-t border-neutral-200 pt-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900">{t("acc_expenses", "Reimbursements in the journal")}</h3>
                  <p className="text-sm text-neutral-800">
                    {t("acc_expenses_hint", "Each expense approved for reimbursement after you switch this on, against the employee, with the receipt. Other currencies are converted to {currency}.").replace("{currency}", setup.currency)}
                  </p>
                </div>
                <Switch checked={draft.expenses} onCheckedChange={(on) => setDraft((d) => ({ ...d, expenses: on }))} aria-label={t("acc_expenses", "Reimbursements in the journal")} />
              </div>
              {draft.expenses && (
                <>
                  <div className="flex items-center justify-between gap-4 max-md:flex-col max-md:items-stretch">
                    <span className="text-sm text-neutral-900">{t("acc_journal", "Journal")}</span>
                    <SearchableSelect
                      className="w-80 max-md:w-full"
                      options={setup.journals.map((j) => ({ value: j.code, label: `${j.code} ${j.name}` }))}
                      value={draft.journalCode ?? ""}
                      placeholder={t("acc_journal_pick", "Choose the journal")}
                      onChange={(v) => setDraft((d) => ({ ...d, journalCode: v }))}
                    />
                  </div>
                  {accountsBlock("expenseAccounts")}
                  {setup.travellers.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-neutral-900">{t("acc_employees", "Employees")}</h4>
                      <p className="text-sm text-neutral-800">{t("acc_employees_hint", "Matched by email where Business Central has one; choose the rest.")}</p>
                      <div className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
                        {setup.travellers.map((tr) => (
                          <div key={tr.id} className="flex items-center justify-between gap-4 px-4 py-2.5 max-md:flex-col max-md:items-stretch">
                            <div className="min-w-0">
                              <div className="truncate text-sm text-neutral-900">{tr.name || tr.email}</div>
                              <div className="truncate text-xs text-neutral-700">{tr.email}</div>
                            </div>
                            <SearchableSelect
                              className="w-80 max-md:w-full"
                              options={[{ value: NONE, label: t("acc_employee_none", "Not matched: not sent") }, ...setup.employees.map((e) => ({ value: e.number, label: `${e.number} ${e.name}`, hint: e.email || undefined }))]}
                              value={draft.employeeMap[String(tr.id)] ?? NONE}
                              onChange={(v) =>
                                setDraft((d) => {
                                  const map = { ...d.employeeMap }
                                  if (v === NONE) delete map[String(tr.id)]
                                  else map[String(tr.id)] = v
                                  return { ...d, employeeMap: map }
                                })
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>

            {draft.invoices && setup.vatRates.length > 0 && (
              <section className="space-y-2 border-t border-neutral-200 pt-5">
                <h3 className="text-sm font-semibold text-neutral-900">{t("acc_vat", "VAT on Travel Code fees")}</h3>
                <p className="text-sm text-neutral-800">
                  {t(
                    "acc_vat_hint",
                    "Choose the Business Central tax code for each rate of VAT we charge: the fee then goes without VAT and Business Central books the VAT itself. Without a code the fee goes at the amount paid, VAT included.",
                  )}
                </p>
                <div className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
                  {setup.vatRates.map((rate) => (
                    <div key={rate} className="flex items-center justify-between gap-4 px-4 py-2.5 max-md:flex-col max-md:items-stretch">
                      <span className="text-sm text-neutral-900">{t("acc_vat_rate", "VAT {rate}%").replace("{rate}", String(Number(rate)))}</span>
                      <SearchableSelect
                        className="w-80 max-md:w-full"
                        options={[{ value: NONE, label: t("acc_vat_included", "Keep in the amount") }, ...setup.taxGroups.map((g) => ({ value: g.code, label: `${g.code} ${g.name}`, hint: g.type }))]}
                        value={draft.vatCodes[rate] ?? NONE}
                        onChange={(v) =>
                          setDraft((d) => {
                            const codes = { ...d.vatCodes }
                            if (v === NONE) delete codes[rate]
                            else codes[rate] = v
                            return { ...d, vatCodes: codes }
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="space-y-2 border-t border-neutral-200 pt-5">
              <h3 className="text-sm font-semibold text-neutral-900">{t("acc_dimension", "Cost centers")}</h3>
              <p className="text-sm text-neutral-800">
                {t("acc_dimension_hint", "The Business Central dimension whose value codes match your Travel Code cost center codes. Unknown codes keep Business Central's defaults.")}
              </p>
              <SearchableSelect
                className="w-80 max-md:w-full"
                options={[{ value: NONE, label: t("acc_dimension_none", "Do not set") }, ...setup.dimensions.map((d) => ({ value: d.code, label: `${d.code} ${d.name}` }))]}
                value={draft.costCenterDimension ?? NONE}
                onChange={(v) => setDraft((d) => ({ ...d, costCenterDimension: v === NONE ? null : v }))}
              />
            </section>

            <div className="flex justify-end border-t border-neutral-200 pt-4">
              <Button onClick={() => void save()} disabled={busy}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                {t("acc_save", "Save")}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function ActivityCard({ connection }: { connection: AccountingConnection }) {
  const t = useBookingT()
  const [kind, setKind] = React.useState<ExportKind | undefined>(undefined)
  const [rows, setRows] = React.useState<AccountingExportRow[] | null>(null)

  React.useEffect(() => {
    setRows(null)
    fetchExports(connection.id, kind)
      .then(setRows)
      .catch((e) => {
        toast.error((e as Error).message)
        setRows([])
      })
  }, [connection.id, kind])

  const kindLabel = (k: ExportKind) =>
    k === "invoice" ? t("acc_kind_invoice", "Invoice") : k === "credit" ? t("acc_kind_credit", "Credit memo") : t("acc_kind_expense", "Reimbursement")

  const status = (row: AccountingExportRow) =>
    row.status === "sent" ? (
      <Badge variant="green" className="whitespace-nowrap">{t("acc_row_sent", "Sent")}</Badge>
    ) : row.status === "failed" ? (
      <Badge variant="destructive" className="whitespace-nowrap">{t("acc_row_failed", "Not sent")}</Badge>
    ) : (
      <Badge variant="secondary" className="whitespace-nowrap">{t("acc_row_skipped", "Skipped")}</Badge>
    )

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <Tabs value={kind ?? "all"} onValueChange={(v) => setKind(v === "all" ? undefined : (v as ExportKind))}>
          <TabsList>
            <TabsTrigger value="all">{t("acc_filter_all", "All")}</TabsTrigger>
            <TabsTrigger value="invoice">{t("acc_flow_invoices", "Invoices")}</TabsTrigger>
            <TabsTrigger value="credit">{t("acc_flow_credits", "Credit memos")}</TabsTrigger>
            <TabsTrigger value="expense">{t("acc_flow_expenses", "Reimbursements")}</TabsTrigger>
          </TabsList>
        </Tabs>
        {rows === null ? (
          <Skeleton className="h-48 rounded-lg" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-neutral-800">{t("acc_activity_empty", "Nothing has been sent yet.")}</p>
        ) : (
          <>
          <ul className="divide-y divide-neutral-200 rounded-md border border-neutral-200 md:hidden">
            {rows.map((row) => (
              <li key={row.id} className="space-y-1 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-neutral-900">{row.reference ?? `#${row.sourceId}`}</div>
                    <div className="text-xs text-neutral-700">
                      {kindLabel(row.kind)} ·{" "}
                      {formatUserDateTime(new Date(row.updatedAt * 1000).toISOString())}
                    </div>
                  </div>
                  {status(row)}
                </div>
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-neutral-800">{row.externalNumber ?? row.error ?? ""}</span>
                  <span className="shrink-0 tabular-nums text-neutral-900">
                    {row.amount !== null && row.currency ? formatMoneyAmount(row.amount, row.currency) : "—"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <Table className="max-md:hidden">
            <TableHeader>
              <TableRow>
                <TableHead>{t("acc_col_when", "When")}</TableHead>
                <TableHead>{t("acc_col_what", "Document")}</TableHead>
                <TableHead className="text-right">{t("acc_col_amount", "Amount")}</TableHead>
                <TableHead>{t("acc_col_status", "Status")}</TableHead>
                <TableHead>{t("acc_col_bc", "In Business Central")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap text-sm">{formatUserDateTime(new Date(row.updatedAt * 1000).toISOString())}</TableCell>
                  <TableCell className="text-sm">
                    <div className="text-neutral-900">{row.reference ?? `#${row.sourceId}`}</div>
                    <div className="text-xs text-neutral-700">{kindLabel(row.kind)}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                    {row.amount !== null && row.currency ? formatMoneyAmount(row.amount, row.currency) : "—"}
                  </TableCell>
                  <TableCell>{status(row)}</TableCell>
                  <TableCell className="text-sm text-neutral-800">{row.externalNumber ?? row.error ?? ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>
        )}
      </CardContent>
    </Card>
  )
}
